import { MasterLinkParams } from '../../Constant'
import { logEvent } from '../analytics/firAnalytics'
import { ANALYTICS_TAGS } from '../../Constant/analytics'
import { normalizeError } from '../errors/errorHelpers'
import { isBrowserEnvironment } from '../windowHelpers'

const FUNCTION_NAME = 'getUniqueInstallId'

/**
 * Gets the GA4 client_id from the _ga cookie.
 * Returns null if _ga cookie is unavailable or invalid.
 */
export const getUniqueInstallId = async (): Promise<string | null> => {
  if (!isBrowserEnvironment()) {
    return null
  }

  try {
    return getGaClientIdFromCookie()
  } catch (error) {
    const normalizedError = normalizeError(error)
    console.error(`Error in ${FUNCTION_NAME}:`, normalizedError)
    logEvent(ANALYTICS_TAGS.firebase_transaction_error, {
      function: FUNCTION_NAME,
      error: normalizedError.message,
    })
    return null
  }
}

/**
 * Parses the _ga cookie to extract the Google Analytics client ID.
 * The _ga cookie format is typically: GA1.2.{client_id}
 * @returns The client ID or null if not found or invalid
 */
const getGaClientIdFromCookie = (): string | null => {
  if (typeof document === 'undefined') {
    return null
  }

  // Check if cookies are enabled in the browser
  if (typeof navigator !== 'undefined' && navigator.cookieEnabled === false) {
    return null
  }

  // Cookie access check
  try {
    const cookieTest = document.cookie
    if (typeof cookieTest !== 'string') {
      return null
    }
  } catch (cookieAccessError) {
    return null
  }

  try {
    const cookies = document.cookie.split(';')
    if (cookies.length === 0 || (cookies.length === 1 && cookies[0].trim() === '')) {
      return null
    }

    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim()

      if (trimmedCookie.startsWith('_ga=')) {
        const gaCookieValue = trimmedCookie.substring(4) // Remove '_ga=' prefix

        // Format: GA1.2.{client_id} or GA1.{domain_parts}.{client_id}
        const parts = gaCookieValue.split('.')

        if (parts.length >= 3 && parts[0] === 'GA1') {
          // The client ID last two parts joined with dot
          const clientId = parts.slice(-2).join('.')
          if (/^\d+\.\d+$/.test(clientId)) {
            return clientId
          }
        }
      }
    }

    return null
  } catch (error) {
    console.error('Error parsing _ga cookie:', error)
    return null
  }
}

export const determineMasterParamsToAdd = (
  mParams: MasterLinkParams | null,
  newUser: boolean
) => {
  if (!mParams) return null
  let linkParams: MasterLinkParams = {}
  if (mParams?.osc) {
    linkParams.osc = mParams.osc
  }
  if (newUser) {
    if (mParams?.pllm_source) {
      linkParams.pllm_source = mParams.pllm_source
    }
    if (mParams?.referrerUserId) {
      linkParams.referrerUserId = mParams.referrerUserId
    }
  }
  return linkParams
}

export const getUserTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}
